import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import { IUserDetails, IUserPayload } from "@Models/IUserDetails";
import { IRoleAssignmentDetails } from "@Models/IRoleDetails";
import { IUserConnectionDetails, IConnectionRequestDetails, RequestType } from "@Models/IUserConnectionDetails";
import { isEmpty } from "@Utils";
import { IEmptyObject } from "@Components/shared/types";

export default class UserService extends ServiceBase {
    /**
     * Fetch users matching the user ids provided, optionally reducing fetch to minimal user data only.
     */
    public async getPublic(userIds: string[], minimal = false, activeOnly = true): Promise<Result<IUserDetails[]>> {
        return await this.requestJson<IUserDetails[]>({
            url: `/api/User/GetPublic/${userIds.join(",")}?minimal=${minimal}&activeOnly=${activeOnly}`,
            method: "GET",
        });
    }

    /**
     * Fetch users matching the user ids provided, with paging. Only available to owning user and admins.
     */
    public async getPrivate(userIds: string[], activeOnly = true): Promise<Result<IUserDetails[]>> {
        return await this.requestJson<IUserDetails[]>({
            url: `/api/User/GetPrivate/${userIds.join(",")}?activeOnly=${activeOnly}`,
            method: "GET",
        });
    }

    /**
     * Return a list of users from the group provided whose first or last
     * names match the search term. The call will always post a minimum of three
     * characters.
     */
    public async searchGroup(
        groupIds: string | string[],
        term: string = null,
        page = 0,
        isAdmin = false
    ): Promise<Result<IUserPayload>> {
        if (isEmpty([groupIds].flat()) || term === null) return null;
        if (term.length < 3) return null;
        const admin = isAdmin ? "&is-admin=true" : "";
        return await this.requestJson<IUserPayload>({
            url: `/api/User/SearchGroup/${[groupIds].flat().join(",")}?term=${term}&page=${page}${admin}`,
            method: "GET",
        });
    }

    /**
     * Return a list of users whose first, last names or email match the search
     * term. The call will always post a minimum of three characters.
     */
    public async searchByNameAndEmail(term = ""): Promise<Result<IUserPayload>> {
        if (term == null || term.length < 3) return null;

        return await this.requestJson<IUserPayload>({
            url: `/api/User/GetUsersByNameAndEmail/?search-term=${term}`,
            method: "GET",
        });
    }

    /**
     * Return a list of users from the user's groups whose first or last
     * names match the search term. The call will always post a minimum of three
     * characters.
     */
    public async searchGroups(userId: string, term = "", page = 0): Promise<Result<IUserPayload>> {
        if (term == null) {
            return null;
        }
        if (term.length < 3) {
            return null;
        }
        return await this.requestJson<IUserPayload>({
            url: `/api/User/SearchGroups/${userId}?term=${term}&page=${page}`,
            method: "GET",
        });
    }

    /**
     * Return a list of users from the system context whose first or last
     * names match the search term. The call will always post a minimum of three
     * characters.
     */
    public async searchSystem(term: string = null, page = 0): Promise<Result<IUserPayload>> {
        if (term == null) {
            return null;
        }
        if (term.length < 3) {
            return null;
        }
        return await this.requestJson<IUserPayload>({
            url: `/api/User/SearchSystem?term=${term}&page=${page}`,
            method: "GET",
        });
    }

    /**
     * Update a user model
     */
    public async update(model: IUserDetails): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/User/Update/${model.userId}`,
            method: "PATCH",
            data: model,
        });
    }

    /**
     * Fetch user role assignments matching the user ids provided.
     */
    public async getRoles(userIds?: string[]): Promise<Result<IRoleAssignmentDetails[]>> {
        return await this.requestJson<IRoleAssignmentDetails[]>({
            url: `/api/User/GetRoles/${userIds.join(",")}`,
            method: "GET",
        });
    }

    /**
     * Assign user roles within group.
     */
    public async assignRoles(userIds: string[], roleId: string, groupId?: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Role/Assign/${groupId}/${userIds.join(",")}?role=${roleId}`,
            method: "POST",
        });
    }

    /**
     * Remove user roles within group.
     */
    public async removeRoles(userIds: string[], roleId: string, groupId?: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/Role/Remove/${groupId}/${userIds.join(",")}?role=${roleId}`,
            method: "POST",
        });
    }

    /**
     * Get a user's connections.
     * @param userId
     */
    public async getUserConnections(userId: string): Promise<Result<IUserConnectionDetails[]>> {
        return await this.requestJson<IUserConnectionDetails[]>({
            url: `/api/UserConnection/Get/${userId}`,
            method: "GET",
        });
    }

    /**
     * Send a connection request from the user to a list of other users.
     * @param userId
     * @param model
     */
    public async sendConnectionRequest(userId: string, model: IConnectionRequestDetails): Promise<Result<number>> {
        return await this.requestJson<number>({
            url: `/api/UserConnection/CreateRequest/${userId}`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Manage a connection request.
     * @param requestType
     * @param userId
     * @param userConnectionId
     */
    public async connectionRequest(
        requestType: RequestType,
        userId: string,
        userConnectionId: string
    ): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/UserConnection/Manage/${userId}?id=${userConnectionId}&request-type=${requestType}`,
            method: "PATCH",
        });
    }

    /**
     * Accept a pending connection request.
     * @param userId
     * @param userConnectionId
     */
    public async acceptConnectionRequest(userId: string, userConnectionId: string): Promise<Result<IEmptyObject>> {
        return await this.connectionRequest("accept", userId, userConnectionId);
    }

    /**
     * Cancel a pending connection request.
     * @param userId
     * @param userConnectionId
     */
    public async cancelConnectionRequest(userId: string, userConnectionId: string): Promise<Result<IEmptyObject>> {
        return await this.connectionRequest("cancel", userId, userConnectionId);
    }

    /**
     * Refuse a pending connection request.
     * @param userId
     * @param userConnectionId
     */
    public async refuseConnectionRequest(userId: string, userConnectionId: string): Promise<Result<IEmptyObject>> {
        return await this.connectionRequest("refuse", userId, userConnectionId);
    }

    /**
     * Delete a connection.
     * @param userId
     * @param userConnectionId
     */
    public async deleteConnection(userId: string, userConnectionId: string): Promise<Result<IEmptyObject>> {
        return await this.connectionRequest("delete", userId, userConnectionId);
    }
}
